import React, { useState, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { wmsRoutes, WmsRoute } from './wmsRoutes';
import WmsErrorBoundary from './WmsErrorBoundary';

const RAIL_WIDTH_EXPANDED = 240;
const RAIL_WIDTH_COLLAPSED = 72;
const COLLAPSED_STORAGE_KEY = 'wms_nav_collapsed';

/**
 * WMS application shell.
 *
 * Permanent collapsible left rail on md+ screens (primary target: iPad
 * landscape). Temporary drawer behind a hamburger on smaller viewports.
 * Sticky top bar displays the current page title sourced from wmsRoutes.
 *
 * Wraps the route outlet in WmsErrorBoundary so a render error in any
 * single screen is contained rather than taking the whole app down.
 *
 * Scope: this layout wraps ONLY /wms/* routes. Container Management
 * routes are intentionally untouched and continue to use their existing
 * ContainerNav component.
 */
export default function WmsLayout(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const handleNavClick = useCallback(
    (path: string) => {
      navigate(path);
      setMobileOpen(false);
    },
    [navigate]
  );

  const handleBackToContainers = useCallback(() => {
    navigate('/containers');
    setMobileOpen(false);
  }, [navigate]);

  const currentRoute: WmsRoute | undefined = useMemo(
    () => wmsRoutes.find((r) => r.path === location.pathname),
    [location.pathname]
  );
  const pageTitle = currentRoute?.title || 'WMS';

  // On desktop, the drawer is permanent and collapsible.
  // On mobile, it's temporary and always renders its full (expanded) content.
  const effectiveCollapsed = isDesktop && collapsed;
  const drawerWidth = effectiveCollapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH_EXPANDED;

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Rail header: brand + collapse toggle */}
      <Toolbar
        sx={{
          px: effectiveCollapsed ? 1 : 2,
          justifyContent: effectiveCollapsed ? 'center' : 'space-between',
          minHeight: 64,
        }}
      >
        {!effectiveCollapsed && (
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}
          >
            Abaris WMS
          </Typography>
        )}
        {isDesktop && (
          <Tooltip
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            placement="right"
          >
            <IconButton
              onClick={toggleCollapsed}
              size="small"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />

      {/* Nav list */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {wmsRoutes.map((route) => {
          const active = location.pathname === route.path;
          const button = (
            <ListItemButton
              key={route.path}
              selected={active}
              onClick={() => handleNavClick(route.path)}
              sx={{
                minHeight: 56,
                mx: 1,
                mb: 0.5,
                borderRadius: 2,
                justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                px: effectiveCollapsed ? 0 : 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: effectiveCollapsed ? 0 : 40,
                  justifyContent: 'center',
                  color: active ? 'inherit' : 'primary.main',
                }}
              >
                {route.icon}
              </ListItemIcon>
              {!effectiveCollapsed && (
                <ListItemText
                  primary={route.label}
                  primaryTypographyProps={{ fontWeight: active ? 600 : 500 }}
                />
              )}
            </ListItemButton>
          );

          return effectiveCollapsed ? (
            <Tooltip key={route.path} title={route.label} placement="right">
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </List>

      <Divider />

      {/* Back to Containers link — only cross-link into the other subsystem */}
      <Box sx={{ p: 1 }}>
        {effectiveCollapsed ? (
          <Tooltip title="Back to Containers" placement="right">
            <ListItemButton
              onClick={handleBackToContainers}
              sx={{
                minHeight: 56,
                borderRadius: 2,
                justifyContent: 'center',
              }}
              aria-label="Back to Containers"
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                <ArrowBackIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={handleBackToContainers}
            sx={{ minHeight: 56, borderRadius: 2, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText
              primary="Containers"
              secondary="Inbound management"
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItemButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop: permanent drawer */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile: temporary drawer */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: RAIL_WIDTH_EXPANDED,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main column: app bar + outlet */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {!isDesktop && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 2 }}
                aria-label="Open navigation"
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, flexGrow: 1 }}
            >
              {pageTitle}
            </Typography>
          </Toolbar>
        </AppBar>

        <WmsErrorBoundary>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              p: { xs: 2, md: 3 },
            }}
          >
            <Outlet />
          </Box>
        </WmsErrorBoundary>
      </Box>
    </Box>
  );
}
